import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core'
import { Store, select } from '@ngrx/store'
import Validator from '@shared/validatorjs/src/validator'
import { Map } from 'immutable'
import { debounceTime, Subject, Subscription } from 'rxjs'
import FormTrait from 'src/app/traits/form.trait'
import {
  FetchBusinessSubcategoryRequest,
  SetDefaultData,
  UpdateBusinessSubcategoryRequest,
  UpdateField,
} from '../business-categories.actions'
import { getUpdateBusinessSubcategoryState } from '../business-categories.selectors'
import {
  FetchBusinessSubcategoryCalculationTypeRequest,
  FetchBusinessSubcategoryTypeRequest,
  FetchFormsRequest,
} from '@shared/shared.actions'
import {
  getBusinessSubcategoryCalculationTypesState,
  getBusinessSubcategoryTypesState,
} from '@shared/shared.select'
import { ActivatedRoute } from '@angular/router'
import { getRowValidations } from '@shared/shared.utils'
import { FetchTemplatesRequest } from '@containers/templates/templates.actions'
import { getTemplatesState } from '@containers/templates/templates.selectors'

@Component({
  selector: 'app-update-business-subcategory',
  templateUrl: './update-business-subcategory.component.html',
  styleUrls: ['./update-business-subcategory.component.css'],
})
export class UpdateBusinessSubcategoryComponent
  extends FormTrait
  implements OnInit, OnDestroy, DoCheck
{
  private state$: Subscription
  public isLoading: boolean
  public types$: Subscription
  public templates$: Subscription
  public types: any[]
  public templates: any[]
  public calculationTypes$: Subscription
  public calculationTypes: any[]
  public calculationTypesBySubcategoryType: any[]
  public businessCategoryId: number
  public sid: number
  public errors: any = {}
  public calcTypesFiltered = false
  public onTemplateAutocomplete$ = new Subject<string>()
  private templateAutocomplete$: Subscription
  public attrNames = {
    subCategoryType: 'subcategory type',
    calculationType: 'calculation type',
    rangeStart: 'range start',
    rangeEnd: 'range end',
    propertyType: 'property type',
    penaltyPercentage: 'penalty percentage',
    minTax: 'Min. Tax',
  }
  public isPropertyTypesOpen = false
  public typesTranslateKeys = []
  public typesTranslateDefaults = {}

  constructor(public store: Store<any>, private route: ActivatedRoute) {
    super()
  }

  get isFixedType() {
    const typeName = this.state.getIn(['data', 'subCategoryType'])
    const isFixed = this.types?.find(
      (type) =>
        type.name === typeName &&
        type.allowedSlotTypes.includes('FIXED_MANUAL'),
    )
    return !!isFixed
  }

  ngOnInit() {
    this.businessCategoryId = parseInt(
      this.route.snapshot.paramMap.get('id'),
      10,
    )
    this.store.dispatch<any>(new SetDefaultData('updateBusinessSubcategory'))
    this.sid = parseInt(this.route.snapshot.paramMap.get('sid'), 10)
    this.store.dispatch<any>(new SetDefaultData('updateBusinessSubcategory'))
    this.state$ = this.store
      .pipe(select(getUpdateBusinessSubcategoryState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')

        const errors = state.get('validationErrors').toJS()

        this.errors = getRowValidations(errors, this.attrNames)

        if (state.get('shouldReload')) {
          this.store.dispatch<any>(
            new FetchBusinessSubcategoryRequest(this.sid),
          )
        }

        if (!this.isLoading && !this.types?.length) {
          this.store.dispatch(new FetchBusinessSubcategoryTypeRequest())
        }

        if (!this.isLoading && !this.calculationTypes?.length) {
          this.store.dispatch(
            new FetchBusinessSubcategoryCalculationTypeRequest(),
          )
        }
      })

    this.store.dispatch<any>(new FetchBusinessSubcategoryRequest(this.sid))

    this.calculationTypes$ = this.store
      .pipe(select(getBusinessSubcategoryCalculationTypesState))
      .subscribe((calculationTypes) => {
        this.calculationTypes = calculationTypes
      })

    this.types$ = this.store
      .pipe(select(getBusinessSubcategoryTypesState))
      .subscribe((types) => {
        this.types = types

        this.typesTranslateKeys = this.types.map((type) => type.trnKey)

        this.typesTranslateDefaults = this.types.reduce((prev, current) => {
          return { ...prev, [current.trnKey]: current.defaultName }
        }, {})
      })

    this.templates$ = this.store
      .pipe(select(getTemplatesState))
      .subscribe((templates) => {
        this.templates = templates.get('data').toJS()
      })

    this.templateAutocomplete$ = this.onTemplateAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(
            new FetchTemplatesRequest({
              filters: {
                or: [{ field: 'name', value: keyword }],
                matchAnd: [{ field: 'type', value: 'BL_TEMPLATE' }],
              },
              pageSize: 10,
              page: 0,
            }),
          )
        }
      })

    this.store.dispatch<any>(
      new FetchTemplatesRequest({
        filters: {
          or: [{ field: 'type', value: 'BL_TEMPLATE' }],
        },
        pageSize: 10,
        page: 0,
      }),
    )
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.types$.unsubscribe()
    this.calculationTypes$.unsubscribe()
    this.templates$.unsubscribe()
    this.templateAutocomplete$.unsubscribe()
  }

  ngDoCheck() {
    if (
      this.types.length &&
      this.calculationTypes.length &&
      !this.isLoading &&
      !this.calcTypesFiltered
    ) {
      const selected = this.types.find(
        (type) => type.name === this.getField(['subCategoryType']),
      )

      if (selected) {
        const { allowedSlotTypes } = selected

        this.calculationTypesBySubcategoryType = this.calculationTypes.filter(
          (type) => allowedSlotTypes.includes(type.name),
        )
      }

      this.calcTypesFiltered = true

      const slots = (this.state.getIn(['data', 'logicSlots']) as any).toJS()
      const newData = slots.map(({ calculationType, ...rest }) => {
        const selectedType = this.calculationTypesBySubcategoryType.find(
          (t) => t.name === calculationType,
        )
        return { calculationType, ...rest, hasRange: selectedType?.hasRange }
      })

      this.store.dispatch(
        new UpdateField(
          'logicSlots',
          newData,
          `updateBusinessSubcategory.data`,
        ),
      )
    }
  }

  public handleFieldChange(
    e: any,
    name: null | string = null,
    row: null | number = null,
  ): void {
    if (row !== null) {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name
            ? e
            : isNaN(e.target.value)
            ? e.target.value
            : parseFloat(e.target.value),
          `updateBusinessSubcategory.data.logicSlots.${row}`,
        ),
      )

      if ((name || e.target.name) === 'calculationType') {
        const selected = this.calculationTypesBySubcategoryType.find(
          (type) => type.name === e.target.value,
        )

        if (selected) {
          this.store.dispatch(
            new UpdateField(
              'hasRange',
              selected.hasRange,
              `updateBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
          this.store.dispatch(
            new UpdateField(
              'rangeStart',
              0,
              `updateBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
          this.store.dispatch(
            new UpdateField(
              'rangeEnd',
              0,
              `updateBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
        }
      }
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          'updateBusinessSubcategory.data',
        ),
      )

      this.store.dispatch(
        new UpdateField(
          'validationErrors',
          this.state.get('validationErrors').delete(name || e.target.name),
          'updateBusinessSubcategory',
        ),
      )

      if (
        this.calculationTypes.length &&
        (name || e.target.name) === 'subCategoryType'
      ) {
        const selected = this.types.find((type) => type.name === e.target.value)

        if (selected) {
          const { allowedSlotTypes } = selected

          this.calculationTypesBySubcategoryType = this.calculationTypes.filter(
            (type) => allowedSlotTypes.includes(type.name),
          )

          this.clearUnsupportedCalcTypes()
        }
      }
    }
  }

  public clearUnsupportedCalcTypes() {
    const slots = (this.state.getIn(['data', 'logicSlots']) as any).toJS()
    const newData = slots.map((slot: any) => {
      if (
        !this.calculationTypesBySubcategoryType.includes(slot.calculationType)
      ) {
        return { ...slot, calculationType: null, hasRange: false }
      }
      return slot
    })

    this.store.dispatch(
      new UpdateField('logicSlots', newData, `updateBusinessSubcategory.data`),
    )
  }

  public rowDelete(row: number) {
    const list = this.state.getIn(['data', 'logicSlots']) as any

    this.store.dispatch(
      new UpdateField(
        'logicSlots',
        list.delete(row).toJS(),
        `updateBusinessSubcategory.data`,
      ),
    )
  }

  public rowAdd() {
    const list = this.state.getIn(['data', 'logicSlots']) as any
    this.store.dispatch(
      new UpdateField(
        'logicSlots',
        list
          .push({
            name: '',
            calculationType: null,
            rangeStart: 0,
            rangeEnd: 0,
            value: 0,
          })
          .toJS(),
        `updateBusinessSubcategory.data`,
      ),
    )
  }

  private validation() {
    const data = this.state.get('data')
    const rules = {
      name: 'required',
      subCategoryType: 'required',
      logicSlots: 'array',
      penaltyPercentage: 'required|numeric',
      'logicSlots.*.name': 'required',
      'logicSlots.*.calculationType': 'required',
      'logicSlots.*.rangeStart': 'required|min:0',
      'logicSlots.*.rangeEnd': 'required|min:0',
      'logicSlots.*.value': 'required|min:0',
    }

    if (!this.isFixedType) {
      rules['propertyType'] = 'required'
    }

    const validator = new Validator(
      {
        name: data.get('name'),
        subCategoryType: data.get('subCategoryType'),
        propertyType: data.get('propertyType'),
        penaltyPercentage: data.get('penaltyPercentage'),
        minTax: data.get('minTax'),
        logicSlots: data.get('logicSlots').toJS(),
      },
      rules,
    )

    validator.setAttributeNames(this.attrNames)

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()
    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(
        new UpdateBusinessSubcategoryRequest(
          this.businessCategoryId,
          this.sid,
          {
            ...payload,
            penaltyPercentage: parseFloat(payload.penaltyPercentage),
            minTax: parseFloat(payload.minTax),
          },
        ),
      )
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          'updateBusinessSubcategory',
        ),
      )
    }
  }
}
